import { createContext, useContext, useEffect, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { LayoutGrid, BookOpen, UserRound, KeyRound, Award, CreditCard, Rocket, Users, Trophy, LifeBuoy, LogOut, Menu, X } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { useAuth } from "@/components/site/auth";
import { api } from "@/lib/api";
import { PACKAGES, COURSES } from "@/data/content";

const NAV = [
  ["", "Overview", LayoutGrid], ["courses", "My Courses", BookOpen], ["certificates", "Certificates", Award],
  ["partner", "Partner Panel", Rocket], ["team", "My Team", Users], ["leaderboard", "Leaderboard", Trophy],
  ["billing", "Billing", CreditCard], ["profile", "Profile & Payout", UserRound], ["password", "Change Password", KeyRound], ["support", "Help & Support", LifeBuoy],
];

const Ctx = createContext(null);
export const useDash = () => useContext(Ctx);

const SideLink = (close) => ([to, label, Icon]) => (
  <NavLink key={to} to={`/dashboard${to ? "/" + to : ""}`} end onClick={close} data-testid={`dash-nav-${to || "overview"}`}
    className={({ isActive }) => `group flex items-center gap-3 rounded-2xl px-2.5 py-2 text-sm font-semibold whitespace-nowrap transition-[background,color,box-shadow] ${isActive ? "text-[var(--on-accent)] shadow-[0_0_22px_-6px_var(--glow-pink)]" : "text-[var(--ink)] hover:bg-[var(--bg-2)]"}`}
    style={({ isActive }) => (isActive ? { background: "var(--btn-bg)" } : undefined)}>
    {({ isActive }) => (<>
      <span className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${isActive ? "bg-[var(--bg)] text-[var(--pink)]" : "border border-[var(--surface-border)] text-[var(--pink)]"}`}><Icon size={16} /></span>
      {label}
    </>)}
  </NavLink>
);

export default function DashboardLayout() {
  const { user, logout, refreshMe } = useAuth();
  const [progress, setProgress] = useState({});
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  useEffect(() => { refreshMe(); api.get("/me/progress").then((r) => setProgress(r.data)); }, []); // eslint-disable-line
  const pkg = PACKAGES.find((p) => p.slug === user.package);
  const courses = COURSES.filter((c) => pkg?.courses.includes(c.slug));
  const approved = user.status === "approved";
  const initials = user.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <Ctx.Provider value={{ user, pkg, courses, approved, progress, setProgress }}>
      <header data-testid="dash-topbar" className="glass !rounded-none !border-x-0 !border-t-0 sticky top-0 z-40">
        <div className="max-w-[1500px] mx-auto flex items-center justify-between px-5 sm:px-8 py-3">
          <Link to="/" data-testid="dash-logo-link"><Logo className="h-10 sm:h-12" /></Link>
          <div className="flex items-center gap-3">
            <div className="text-right"><p data-testid="dash-user-name" className="font-semibold text-sm sm:text-base leading-tight">{user.name}</p><p className="text-[11px] text-[var(--pink)] font-bold">ID: {user.referral_code}</p></div>
            <div className="w-11 h-11 rounded-full grid place-items-center font-bold text-[var(--on-accent)] ring-2 ring-[var(--pink)]/40" style={{ background: "var(--btn-bg)" }}>{initials}</div>
            <button data-testid="dash-menu-btn" onClick={() => setOpen(true)} className="lg:hidden w-10 h-10 grid place-items-center rounded-xl glass"><Menu size={18} /></button>
          </div>
        </div>
      </header>

      {open && <div onClick={close} className="fixed inset-0 z-40 bg-black/50 lg:hidden" />}

      <div data-testid="dashboard-layout" className="max-w-[1500px] mx-auto px-4 sm:px-8 pt-8 grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-8">
        <aside className={`glass p-4 min-w-0 fixed inset-y-0 left-0 z-50 w-[280px] rounded-none overflow-y-auto transition-transform lg:static lg:z-auto lg:w-auto lg:rounded-3xl lg:sticky lg:top-24 lg:self-start lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
          <div className="flex justify-end lg:hidden"><button onClick={close} className="text-[var(--muted)]"><X size={20} /></button></div>
          <nav className="flex flex-col gap-1.5">{NAV.map(SideLink(close))}</nav>
          <button data-testid="logout-btn" onClick={logout} className="btn-neon w-full justify-center text-sm mt-5"><LogOut size={15} /> Log Out</button>
        </aside>
        <section className="min-w-0"><Outlet /></section>
      </div>
    </Ctx.Provider>
  );
}

export const courseProgress = (progress, c) => {
  const done = (progress[c.slug] || []).length;
  return { done, total: c.modules.length, pct: Math.round((done / c.modules.length) * 100) };
};
